import { Button, Input, Popconfirm, Space, Table, Tag } from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteTravel, loadAllTravel } from "../../redux/rtk/features/travel/travelSlice";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
import ViewBtn from "../Buttons/ViewBtn";
import BtnViewSvg from "../UI/Button/btnViewSvg";

function GetAllTravelRequests({ form, setIsEditMode, setCurrentTravelId }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const [searchEmployee, setSearchEmployee] = useState("");
	const list = useSelector((state) => state.travel?.list || []);
	const loading = useSelector((state) => state.travel?.loading || false);
	const staffList = useSelector((state) => state.users?.list || []);

	useEffect(() => {
		dispatch(loadAllTravel());
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => e.id === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName
			);
		}
		return employeeId;
	};

	const handleEdit = (record) => {
		setIsEditMode(true);
		setCurrentTravelId(record.id || record.TravelId);
		form.setFieldsValue({
			employeeId: record.employeeId || record.EmployeeId,
			fromLocation: record.fromLocation || record.FromLocation,
			toLocation: record.toLocation || record.ToLocation,
			travelDate: record.travelDate ? dayjs(record.travelDate) : null,
			returnDate: record.returnDate ? dayjs(record.returnDate) : null,
			purpose: record.purpose || record.Purpose,
			estimatedCost: record.estimatedCost || record.EstimatedCost,
			travelMode: record.travelMode || record.TravelMode,
		});
	};

	const handleDelete = async (id) => {
		const resp = await dispatch(deleteTravel(id));
		if (resp.payload?.message === "success") {
			dispatch(loadAllTravel());
		}
	};

	const columns = [
		{
			id: 1,
			title: "S.No",
			dataIndex: "serial",
			key: "serial",
		},
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
		},
		{
			title: "From",
			dataIndex: "fromLocation",
			key: "fromLocation",
		},
		{
			title: "To",
			dataIndex: "toLocation",
			key: "toLocation",
		},
		{
			title: "Travel Date",
			dataIndex: "travelDate",
			key: "travelDate",
			render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
		},
		{
			title: "Return Date",
			dataIndex: "returnDate",
			key: "returnDate",
			render: (date) => (date ? dayjs(date).format("DD-MM-YYYY") : "-"),
		},
		{
			title: "Purpose",
			dataIndex: "purpose",
			key: "purpose",
			render: (purpose) =>
				purpose
					? purpose.substring(0, 30) + (purpose.length > 30 ? "..." : "")
					: "-",
		},
		{
			title: "Estimated Cost (₹)",
			dataIndex: "estimatedCost",
			key: "estimatedCost",
			render: (cost) => {
				if (!cost) return "-";
				return `₹ ${Number(cost).toLocaleString("en-IN")}`;
			},
		},
		{
			title: "Travel Mode",
			dataIndex: "travelMode",
			key: "travelMode",
		},
		{
			title: "Status",
			dataIndex: "approvalStatus",
			key: "approvalStatus",
			render: (status) => {
				if (status === "APPROVED") {
					return <Tag color="green">Approved</Tag>;
				} else if (status === "REJECTED") {
					return <Tag color="red">Rejected</Tag>;
				} else {
					return <Tag color="orange">Pending</Tag>;
				}
			},
		},
		{
			title: "Actions",
			key: "action",
			render: (_, record) => {
				const id = record.id || record.TravelId;
				return (
					<Space>
						<ViewBtn
							path={`/admin/travel/${id}`}
							text="View"
							icon={<BtnViewSvg />}
						/>
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={() => handleEdit(record)}
						/>
						<Popconfirm
							title="Are you sure you want to delete this travel request?"
							onConfirm={() => handleDelete(id)}
							okText="Yes"
							cancelText="No"
						>
							<Button type="link" danger icon={<DeleteOutlined />} />
						</Popconfirm>
					</Space>
				);
			},
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || i.TravelId || index,
			serial: index + 1,
			employeeName: mapEmployeeName(i.employeeId || i.EmployeeId),
		}));

	const filteredData = addKeys(list).filter((item) => {
		const employeeName = (item.employeeName || "").toLowerCase();
		return employeeName.includes(searchEmployee.toLowerCase());
	});

	return (
		<div className="travel-table-container">
			<div className="text-center my-2 flex justify-between items-center">
				<h5 className="text-color-2 text-xl mb-2">Travel Requests List</h5>
				{list && (
					<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
						<ColVisibilityDropdown
							options={columns}
							columns={columns}
							columnsToShowHandler={columnsToShowHandler}
						/>
						<Input
							placeholder="Search by Employee Name"
							value={searchEmployee}
							onChange={(e) => setSearchEmployee(e.target.value)}
							style={{ width: 200 }}
						/>
					</div>
				)}
			</div>

			<Table
				scroll={{ x: true }}
				loading={loading}
				columns={columnsToShow}
				dataSource={filteredData}
				pagination={{
					pageSize: 10,
					showSizeChanger: true,
					showTotal: (total, range) =>
						`${range[0]}-${range[1]} of ${total} items`,
					pageSizeOptions: ["10", "20", "50", "100"],
				}}
			/>
		</div>
	);
}

export default GetAllTravelRequests;

